import { useEffect } from "react";
import { postMqttHourly } from "@client/api/endpoints";
import { isStatsFresh, useStatsStore } from "@client/store/stats";
import { useUserStore } from "@client/store/user";

/**
 * TTL-cached fetch pro `/api/mqtt/hourly`. Pro aktuální den TTL 60s, pro historický
 * den bez TTL. Viz docs/plans/CHART-DATA-REFACTOR.md (Fáze 5).
 */
export const useStatsHourly = (
  topicId: string,
  dateMs?: number
): { stats: dailyStats[]; loading: boolean } => {
  const user = useUserStore((s) => s.user);
  const dayKey = dateMs ?? "today";
  const cached = useStatsStore((s) => s.cache.get(`hourly|${topicId}|${dayKey}`));

  useEffect(() => {
    if (!user || !topicId) return;
    const cacheKey = `hourly|${topicId}|${dayKey}`;
    const existing = useStatsStore.getState().get(cacheKey);
    if (existing?.kind === "hourly" && isStatsFresh(existing, Date.now())) return;

    let aborted = false;
    const requestDate = dateMs ?? Date.now();
    postMqttHourly({ topicId, date: requestDate })
      .then((res) => {
        if (aborted || !res.success) return;
        useStatsStore.getState().put(cacheKey, {
          kind: "hourly",
          data: res.responseObject,
          dayMs: requestDate,
          fetchedAt: Date.now(),
        });
      })
      .catch(() => {
        // network error — necachovat
      });

    return () => {
      aborted = true;
    };
  }, [user, topicId, dayKey, dateMs]);

  const stats = cached?.kind === "hourly" ? cached.data : [];
  return { stats, loading: !cached };
};
